/**
 * src/modules/status-key.ts — ETAP 3 (2026-08-10): tożsamością statusu jest KLUCZ,
 * a nazwa jest tylko napisem, który user może zmienić.
 *
 * BLIŹNIAK `core/status_constants.klucz_statusu` — ta sama tabela zapisów, ta sama
 * kolejność dopasowania. Rozjechanie się tych dwóch stron oznacza, że licznik po
 * stronie serwera policzy co innego niż pasek po stronie przeglądarki.
 *
 * DLACZEGO TO POWSTAŁO: `normalizeStatusLabel` mapował surowy zapis na ZAKUTĄ NA
 * SZTYWNO polską nazwę („packed" → „Spakowane"). Zmiana nazwy statusu w drzewku
 * była więc niewidoczna — user przemianowywał „Spakowane" na „Zapakowane", zapisywał,
 * a lista zamówień dalej pokazywała „Spakowane". Nie było błędu; po prostu nic się
 * nie działo.
 *
 * TERAZ: surowy zapis → KLUCZ → nazwa z drzewka firmy. Zakuta mapa polskich nazw
 * zostaje WYŁĄCZNIE jako zapas na moment, w którym drzewko jeszcze się nie wczytało
 * (pierwszy render listy potrafi wyprzedzić odpowiedź `/api/order-status-tree`).
 */

/** Surowy zapis (znormalizowany) → KLUCZ. Odpowiednik `ORDER_STATUS_KEYS`. */
const KLUCZE_ZAPISOW: Record<string, string> = {
  // new
  'new': 'new', 'nowe': 'new', 'bought': 'new',
  // confirmed / awaiting_processing
  'confirmed': 'confirmed', 'potwierdzone': 'confirmed',
  'awaiting_processing': 'awaiting_processing', 'awaitingprocessing': 'awaiting_processing',
  // processing
  'processing': 'processing', 'wrealizacji': 'processing',
  'filledin': 'processing', 'readyforprocessing': 'processing',
  // packed
  'packed': 'packed', 'spakowane': 'packed',
  // ready_to_ship — „Do wysłania" to OSOBNY status, nie alias „Spakowane"
  // (test charakteryzacyjny etapu 1 słusznie zaprotestował przy zrównaniu).
  'ready_to_ship': 'ready_to_ship', 'readytoship': 'ready_to_ship',
  'readyforshipment': 'ready_to_ship',
  'dowyslania': 'ready_to_ship', 'do wyslania': 'ready_to_ship', 'do wysłania': 'ready_to_ship',
  'dowysylki': 'ready_to_ship', 'do wysylki': 'ready_to_ship', 'do wysyłki': 'ready_to_ship',
  // shipped
  'shipped': 'shipped', 'sent': 'shipped', 'wyslane': 'shipped',
  'wysane': 'shipped', 'wysaane': 'shipped', 'wyslanetest': 'shipped',
  // ready_for_pickup
  'ready_for_pickup': 'ready_for_pickup', 'readyforpickup': 'ready_for_pickup',
  'gotowedoodbioru': 'ready_for_pickup', 'doodbioru': 'ready_for_pickup',
  // picked_up
  'picked_up': 'picked_up', 'pickedup': 'picked_up', 'odebrane': 'picked_up',
  // delivered
  'delivered': 'delivered', 'dostarczone': 'delivered', 'dostarczona': 'delivered',
  'dostarczonetest': 'delivered',
  // cancelled
  'cancelled': 'cancelled', 'canceled': 'cancelled',
  'anulowane': 'cancelled', 'anulowana': 'cancelled',
  // returned
  'returned': 'returned', 'return': 'returned', 'zwrot': 'returned', 'zwrocona': 'returned',
};

/**
 * Nazwy ZAPASOWE — używane tylko dopóki drzewko statusów firmy się nie wczyta.
 * Gdy się wczyta, wygrywa nazwa ustawiona przez usera.
 */
const NAZWY_ZAPASOWE: Record<string, string> = {
  new: 'Nowe',
  confirmed: 'Potwierdzone',
  awaiting_processing: 'Potwierdzone',
  processing: 'W realizacji',
  packed: 'Spakowane',
  ready_to_ship: 'Do wysłania',
  shipped: 'Wysłane',
  ready_for_pickup: 'Gotowe do odbioru',
  picked_up: 'Odebrane',
  delivered: 'Dostarczone',
  cancelled: 'Anulowane',
  returned: 'Zwrot',
};

/** Składa diakrytyki i sprowadza do porównywalnej postaci (jak strona serwera). */
function _plaski(txt: string): string {
  let s = String(txt || '').trim().toLowerCase();
  try {
    s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
  } catch (_e) { /* starsze silniki — zostaje forma z diakrytykami */ }
  return s.replace(/ł/g, 'l');
}

/**
 * Sprowadza cokolwiek zapisanego w zamówieniu do stabilnego KLUCZA.
 *
 * Ta sama kolejność kandydatów co po stronie serwera: najpierw forma bez spacji
 * i podkreśleń („wrealizacji"), potem płaska ze spacjami („w realizacji"), potem
 * surowy lowercase. Nierozpoznane zostaje płaskim napisem — dzięki temu WŁASNE
 * statusy firmy („Dosłać coś") też mają stabilną, powtarzalną tożsamość.
 */
export function kluczStatusu(wartosc: any): string {
  const s = String(wartosc == null ? '' : wartosc).trim();
  if (!s) return '';
  const plaski = _plaski(s);
  const kandydaci = [plaski.replace(/[ _]/g, ''), plaski, s.toLowerCase()];
  for (let i = 0; i < kandydaci.length; i++) {
    const k = KLUCZE_ZAPISOW[kandydaci[i]];
    if (k) return k;
  }
  // Tablica wyżej zna tylko nazwy DOMYŚLNE. Nazwa nadana przez firmę („Zapakowane")
  // nie ma prawa się w niej znaleźć — jej klucz znajdziemy wyłącznie w drzewku.
  // Zwykle nie schodzimy tu wcale, bo API oddaje `status_key` wprost; to jest siatka
  // pod ścieżki lokalne (np. status ustawiony przed odświeżeniem listy).
  const mapa = _drzewko();
  const bezpo = mapa[plaski.replace(/[^a-z0-9]/g, '')];
  if (bezpo && bezpo.key && !bezpo.parent_key) return String(bezpo.key).toLowerCase();
  for (const mapKey in mapa) {
    const wezel = mapa[mapKey];
    if (!wezel || !wezel.key || wezel.parent_key) continue;
    if (_plaski(String(wezel.name || '')) === plaski) return String(wezel.key).toLowerCase();
  }
  return plaski;
}

function _drzewko(): Record<string, any> {
  const w = window as any;
  return (w.__sbStateExt && w.__sbStateExt._statusTreeMap) || {};
}

/**
 * KLUCZ → nazwa, którą user widzi. Bierzemy KORZEŃ drzewka (`parent_key` puste):
 * pod korzeniem wiszą mapowania marketplace, których nazwy są techniczne
 * („SENT", „READY_FOR_SHIPMENT") i nie nadają się na etykietę w panelu.
 */
export function nazwaDlaKlucza(klucz: string): string {
  if (!klucz) return '';
  const mapa = _drzewko();
  const wezel = mapa[String(klucz).toLowerCase()];
  if (wezel && wezel.name && !wezel.parent_key && String(wezel.category || 'order') === 'order') {
    return String(wezel.name);
  }
  return NAZWY_ZAPASOWE[klucz] || '';
}

/**
 * Surowy zapis statusu → nazwa do pokazania. Nierozpoznany status wraca w oryginale,
 * żeby własny status firmy nie zmienił się w pusty napis.
 */
export function nazwaStatusu(wartosc: any): string {
  const surowy = String(wartosc == null ? '' : wartosc).trim();
  if (!surowy) return NAZWY_ZAPASOWE.new;
  const klucz = kluczStatusu(surowy);
  return nazwaDlaKlucza(klucz) || surowy;
}

/**
 * Etykieta statusu na odznace — z rozstrzygnięciem sporu „tłumaczenie kontra nazwa firmy".
 *
 * ZMIERZONE, INACZEJ NIEWIDOCZNE: `tStatus('order', 'packed', …)` znajduje w `pl.json`
 * klucz `status.order.packed` i zwraca „Spakowane" — NADPISUJĄC nazwę, którą user
 * przed chwilą ustawił w drzewku. Przemianowanie było więc niewidoczne w tabeli,
 * na kartach i na liście wirtualnej nawet po naprawie samego resolvera nazw.
 *
 * ROZSTRZYGNIĘCIE: słownik tłumaczy DOMYŚLNE nazwy statusów. W chwili, w której user
 * nadał statusowi własną nazwę, przestaje to być tłumaczenie, a staje się danymi firmy
 * — i wygrywa w każdym języku. Dopóki nazwa jest domyślna, tłumaczenie działa jak dotąd
 * (angielski panel dalej pokazuje „Packed", nie „Spakowane").
 */
export function etykietaStatusu(klucz: string, nazwa: string): string {
  const wlasna = String(nazwa || '');
  const domyslna = NAZWY_ZAPASOWE[String(klucz || '')] || '';
  if (wlasna && domyslna && wlasna !== domyslna) return wlasna;
  const w = window as any;
  if (w.__sbI18n && typeof w.__sbI18n.tStatus === 'function') {
    return w.__sbI18n.tStatus('order', klucz, wlasna || klucz);
  }
  return wlasna || String(klucz || '');
}

if (typeof window !== 'undefined') {
  const w = window as any;
  if (!w.__sbExports) w.__sbExports = {};
  w.__sbExports.kluczStatusu = kluczStatusu;
  w.__sbExports.nazwaDlaKlucza = nazwaDlaKlucza;
  w.__sbExports.nazwaStatusu = nazwaStatusu;
  w.__sbExports.etykietaStatusu = etykietaStatusu;
  // Legacy app.js sięga po gołe `window.*` — bez tego stare ścieżki renderu
  // dalej trzymałyby zakute polskie nazwy.
  if (typeof w.kluczStatusu !== 'function') w.kluczStatusu = kluczStatusu;
  if (typeof w.nazwaStatusu !== 'function') w.nazwaStatusu = nazwaStatusu;
}
